/**
 * OutputScreen — Output da execução com 5 abas (F3-FE-3 + F4-FE-1 + F4-FE-2).
 *
 * Porta a OutputScreen do protótipo, lendo:
 *   GET /executions/{id}/output[?artifacts_limit=N&artifacts_offset=M]
 *   → { execution_id, pull_request, code_artifacts, artifacts_total,
 *        compliance_checks, audit_events, replay_events }
 *
 * 5 abas (fiel ao protótipo + F4):
 *   1. Pull Request (PrTab)       — PR real com pr_url clicável, branches, commit,
 *                                   badge "PR real" vs "Simulado" [F4-FE-1]
 *   2. Código (CodeArtifactsTab)  — diffs reais por arquivo, paginado [F4-FE-2]
 *   3. Compliance Check (ComplianceTab) — 11 checks com status passed/failed/warning
 *   4. Audit Report (AuditTab)    — timeline de eventos de auditoria
 *   5. Replay Timeline (TimelineTab) — eventos determinísticos do replay
 *
 * Segurança (OWASP A09):
 *   - NUNCA exibe wallet_txn_id (já filtrado na API)
 *   - NUNCA exibe sandbox_run_id no tooltip/detalhe do usuário
 *   - 403 → ForbiddenBanner
 *
 * Acessibilidade:
 *   - role="tablist" / role="tab" / role="tabpanel" + aria-selected + aria-controls
 *   - Navegação por teclado nas abas (ArrowLeft/ArrowRight + Enter/Space)
 *   - Links externos: aria-label explícito + rel="noopener noreferrer"
 *   - Regiões de diff: role="region" + aria-label
 *   - prefers-reduced-motion respeitado nas animações
 *
 * Standards: Clean, NFR (a11y), OWASP A09.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GitPullRequest, ShieldCheck, FileText, Clock,
  CheckCircle, XCircle, AlertCircle, ExternalLink,
  Play, Download, Info, FileCode, ChevronLeft, ChevronRight,
  GitBranch, Hash, GitCommit, AlertTriangle, FileSearch,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { QuemFezDaExecucao } from '@/components/QuemFezSection';
import { Card, Btn, Badge, ForbiddenBanner, AiTruncationNotice } from '@/components/ui';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { RichContent } from '@/components/MermaidView';
import { useExecutionOutput, useExecutionArtifacts, useWaiveComplianceCheck, useExecution, useDeliveryStatus, usePushToGithub, useBaixarCorrecoesPendentes, useBaixarAtividadesComplementares, useBaixarSetupIaAmbiente, useBaixarProjetoDaEntrega } from '@/api/executions';
import { useGenerateRecommendations, useGenerateReport } from '@/api/analysis';
import { AiProgress } from '@/components/ui/AiProgress';
import { downloadFile, slugify, artifactsToMarkdown, buildAnalysisHtml } from './analysisExport';
import { MOSTRAR_ENGINEERING_STANDARDS } from '@/lib/engineeringStandards';
import { useNavigationContext } from '@/navigation';
import { SeloDeGarantia, AJUDA_FINALIZACAO_NO_CLIENTE } from '@/components/SeloDeGarantia';

// ─── Constantes ───────────────────────────────────────────────────────────────

const TABS = [
  { id: 'pr',         label: 'Pull Request',       Icon: GitPullRequest },
  { id: 'code',       label: 'Código',             Icon: FileCode       },
  { id: 'compliance', label: 'Compliance Check',   Icon: ShieldCheck    },
  { id: 'audit',      label: 'Audit Report',        Icon: FileText       },
  { id: 'replay',     label: 'Replay Timeline',     Icon: Play           },
].filter((t) => MOSTRAR_ENGINEERING_STANDARDS || t.id !== 'compliance');

// Abas do modo ANÁLISE (delivery_scope === 'analise'): "Análise" sempre; a
// "Auditoria" só entra quando HÁ eventos (senão vinha uma aba vazia — a execução
// de análise nem sempre gera trilha de audit_log). Ver ANALYSIS_TABS() abaixo.
const ANALYSIS_TAB_ANALISE = { id: 'analise', label: 'Análise',   Icon: FileSearch };
const ANALYSIS_TAB_AUDIT   = { id: 'audit',   label: 'Auditoria', Icon: FileText   };

function analysisTabs(hasAudit) {
  return hasAudit
    ? [ANALYSIS_TAB_ANALISE, ANALYSIS_TAB_AUDIT]
    : [ANALYSIS_TAB_ANALISE];
}

const ARTIFACTS_PER_PAGE = 10;

const COMPLIANCE_STATUS_META = {
  passed:  { label: 'APROVADO',  color: C.cyber, Icon: CheckCircle  },
  failed:  { label: 'REPROVADO', color: C.hot,   Icon: XCircle      },
  warning: { label: 'ATENÇÃO',   color: C.neon,  Icon: AlertCircle  },
};

/** Mapeamento action → label e cor. */
const ACTION_META = {
  A: { label: 'CRIADO',     color: C.cyber    },
  M: { label: 'MODIFICADO', color: C.quantum  },
  D: { label: 'DELETADO',   color: C.hot      },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Parseia um unified diff (formato patch) em hunks de linhas classificadas.
 * Retorna array de { type: 'hunk'|'add'|'del'|'ctx'|'meta', text: string }.
 */
function parseDiff(diff) {
  if (!diff || typeof diff !== 'string') return [];
  return diff.split('\n').map((line) => {
    if (line.startsWith('@@'))  return { type: 'hunk', text: line };
    if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('diff ') || line.startsWith('index '))
      return { type: 'meta', text: line };
    if (line.startsWith('+'))   return { type: 'add',  text: line };
    if (line.startsWith('-'))   return { type: 'del',  text: line };
    return { type: 'ctx', text: line };
  });
}

// ─── PrTab — aba Pull Request (F4-FE-1) ──────────────────────────────────────

/**
 * PrTab — aba Pull Request com dados reais: pr_url, branches, commit, badges.
 */
function PrTab({ pr, prs }) {
  const list = (prs || []);

  // Multi-repo (Bloco C): mais de 1 PR → 1 card por repositório. O nome curto do
  // repo já vem prefixado no title de cada item.
  if (list.length > 1) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.neutral }}>
          {list.length} Pull Requests{' '}
          <span style={{ fontWeight: 400, color: C.neutralMuted }}>(1 por repositório)</span>
        </div>
        {list.map((p, i) => (
          <PrCard key={p.pr_id ?? p.number ?? i} pr={p} />
        ))}
      </div>
    );
  }

  // 0 ou 1 PR: comportamento single inalterado. Fallback ao array quando o
  // singular vier vazio, para não regredir respostas antigas.
  const single = pr ?? list[0] ?? null;

  if (!single) {
    return (
      <div style={{ padding: '20px 22px', borderRadius: 10,
        background: 'rgba(123,59,255,0.08)', border: `1px solid ${C.quantum ?? '#7b3bff'}44` }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.neutral, marginBottom: 8 }}>
          Nenhum Pull Request nesta execução
        </div>
        <div style={{ fontSize: 13, color: C.neutralDim, lineHeight: 1.6 }}>
          Se a execução <strong style={{ color: C.neutral }}>concluiu com sucesso</strong>, ela rodou em
          <strong style={{ color: C.neutral }}> modo simulado</strong> — o passo de abrir PR é pulado quando o
          projeto não tem um repositório Git conectado. Para gerar um <strong style={{ color: C.neutral }}>PR
          real</strong> (com o código e a pasta <code style={{ background: C.blackElev2, padding: '1px 5px',
          borderRadius: 4 }}>docs/</code> de escopo + artefatos), conecte um repositório Git ao projeto
          (etapa "Repositório Git" da jornada) e execute novamente. Se a execução
          <strong style={{ color: C.neutral }}> falhou</strong>, veja {MOSTRAR_ENGINEERING_STANDARDS ? 'a aba de compliance / ' : ''}o diagnóstico de falha.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PrCard pr={single} />
    </div>
  );
}

/**
 * PrCard — card de um único Pull Request (banner simulado/real + Card com
 * branches, commit, links e resumo). Reutilizado 1×N no modo multi-repo.
 */
function PrCard({ pr }) {
  const isReal = !pr.is_simulated;

  return (
    <>

      {/* ── Banner informativo de simulado/real ── */}
      {pr.is_simulated ? (
        <div
          role="status"
          aria-label="PR gerado em modo simulado"
          style={{
            display:         'flex',
            alignItems:      'center',
            gap:             8,
            padding:         '10px 14px',
            borderRadius:    8,
            backgroundColor: `${C.digital}11`,
            border:          `1px solid ${C.digital}33`,
          }}
        >
          <Info size={14} aria-hidden="true" style={{ color: C.digital, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: C.digitalLight }}>
            Este PR foi gerado em modo simulado (sandbox de codegen). O conteúdo reflete o planejamento aprovado nos Quality Gates.
          </span>
        </div>
      ) : (
        <div
          role="status"
          aria-label="PR real criado no provedor"
          style={{
            display:         'flex',
            alignItems:      'center',
            gap:             8,
            padding:         '10px 14px',
            borderRadius:    8,
            backgroundColor: `${C.cyber}11`,
            border:          `1px solid ${C.cyber}33`,
          }}
        >
          <CheckCircle size={14} aria-hidden="true" style={{ color: C.cyber, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: C.cyber }}>
            Pull Request real criado no repositório.
            {pr.provider && (
              <span style={{ color: C.neutralDim }}> Provedor: {pr.provider}</span>
            )}
          </span>
        </div>
      )}

      {/* ── Header do PR ── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          {/* Ícone */}
          <div style={{
            width:           40,
            height:          40,
            borderRadius:    8,
            backgroundColor: `${C.cyber}18`,
            border:          `1px solid ${C.cyber}44`,
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            flexShrink:      0,
          }}>
            <GitPullRequest size={18} aria-hidden="true" style={{ color: C.cyber }} />
          </div>

          {/* Título e metadados */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: C.neutral }}>
                {pr.title ?? 'Pull Request'}
              </span>
              {/* Badge: real vs simulado */}
              <span
                aria-label={isReal ? 'PR real' : 'PR simulado'}
                style={{
                  fontSize:        10,
                  fontWeight:      700,
                  padding:         '2px 8px',
                  borderRadius:    4,
                  backgroundColor: isReal ? `${C.cyber}22` : `${C.digital}22`,
                  border:          `1px solid ${isReal ? C.cyber : C.digital}44`,
                  color:           isReal ? C.cyber : C.digital,
                  textTransform:   'uppercase',
                  letterSpacing:   '0.04em',
                  whiteSpace:      'nowrap',
                  flexShrink:      0,
                }}
              >
                {isReal ? 'PR REAL' : 'SIMULADO'}
              </span>
            </div>

            {/* Número / external_pr_id */}
            {(pr.pr_id || pr.external_pr_id) && (
              <div style={{ fontSize: 12, color: C.neutralMuted, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Hash size={11} aria-hidden="true" />
                <span>
                  {pr.external_pr_id
                    ? `PR #${pr.external_pr_id}`
                    : `PR ${pr.pr_id}`}
                  {pr.pr_id && pr.external_pr_id && (
                    <span style={{ color: C.neutralMuted }}> · id interno: {pr.pr_id}</span>
                  )}
                </span>
              </div>
            )}

            {/* Branches: head → base */}
            {(pr.head_branch || pr.base_branch) && (
              <div style={{
                display:    'flex',
                alignItems: 'center',
                gap:        6,
                fontSize:   12,
                color:      C.neutralDim,
                marginBottom: 8,
                flexWrap:   'wrap',
              }}>
                <GitBranch size={11} aria-hidden="true" style={{ color: C.neon, flexShrink: 0 }} />
                {pr.head_branch && (
                  <code style={{ color: C.neon, backgroundColor: `${C.neon}18`, padding: '1px 6px', borderRadius: 3, fontFamily: 'monospace', fontSize: 11 }}>
                    {pr.head_branch}
                  </code>
                )}
                {pr.head_branch && pr.base_branch && (
                  <span style={{ color: C.neutralMuted }}>→</span>
                )}
                {pr.base_branch && (
                  <code style={{ color: C.neutralDim, backgroundColor: C.blackElev2, padding: '1px 6px', borderRadius: 3, fontFamily: 'monospace', fontSize: 11 }}>
                    {pr.base_branch}
                  </code>
                )}
              </div>
            )}

            {/* Commit SHA */}
            {pr.commit_sha && (
              <div style={{
                display:    'flex',
                alignItems: 'center',
                gap:        6,
                fontSize:   11,
                color:      C.neutralMuted,
                marginBottom: 8,
              }}>
                <GitCommit size={11} aria-hidden="true" />
                <code style={{ fontFamily: 'monospace' }}>{pr.commit_sha.slice(0, 12)}</code>
              </div>
            )}

            {/* Links: PR real e diff */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {pr.pr_url && (
                <a
                  href={pr.pr_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Abrir Pull Request ${pr.external_pr_id ? `#${pr.external_pr_id}` : ''} no provedor (abre nova aba)`}
                  style={{
                    display:     'inline-flex',
                    alignItems:  'center',
                    gap:         4,
                    fontSize:    12,
                    color:       C.cyber,
                    textDecoration: 'none',
                    fontWeight:  600,
                  }}
                >
                  <ExternalLink size={12} aria-hidden="true" />
                  Ver Pull Request
                </a>
              )}
              {pr.diff_url && (
                <a
                  href={pr.diff_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Abrir diff do Pull Request (abre nova aba)"
                  style={{
                    display:     'inline-flex',
                    alignItems:  'center',
                    gap:         4,
                    fontSize:    12,
                    color:       C.neutralDim,
                    textDecoration: 'none',
                  }}
                >
                  <ExternalLink size={12} aria-hidden="true" />
                  Ver diff
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Body do PR (quando o provedor retorna) OU resumo gerado a partir dos dados reais */}
        <div style={{
          marginTop:  14,
          paddingTop: 14,
          borderTop:  `1px solid ${C.blackBorder}`,
          fontSize:   13,
          color:      C.neutralDim,
          lineHeight: 1.65,
          whiteSpace: pr.body ? 'pre-wrap' : 'normal',
          wordBreak:  'break-word',
        }}>
          {pr.body ? pr.body : (
            <>
              <strong style={{ color: C.neutral }}>Resumo:</strong> {pr.title || 'entrega da execução'}.
              {' '}Alterações: <span style={{ color: C.cyber }}>+{pr.additions ?? 0}</span> /{' '}
              <span style={{ color: C.hot }}>−{pr.deletions ?? 0}</span> em{' '}
              <code style={{ background: C.blackElev2, padding: '1px 5px', borderRadius: 4 }}>
                {pr.branch || pr.head_branch || 'branch da execução'}
              </code>
              {pr.commit_sha ? <> · commit <code style={{ background: C.blackElev2, padding: '1px 5px', borderRadius: 4 }}>{String(pr.commit_sha).slice(0, 10)}</code></> : null}.
              <br />
              Aprovado nos Quality Gates (Negócio · Tecnologia · Squads · Escopo · Final).{' '}
              {MOSTRAR_ENGINEERING_STANDARDS && (
                <>Detalhes de conformidade na aba <strong style={{ color: C.neutral }}>Compliance Check</strong>;{' '}</>
              )}
              {MOSTRAR_ENGINEERING_STANDARDS ? 'código' : 'Código'} e
              diffs na aba <strong style={{ color: C.neutral }}>Código</strong>.
            </>
          )}
        </div>
      </Card>
    </>
  );
}

// ─── CodeArtifactsTab — aba Código com diffs reais (F4-FE-2) ─────────────────

/**
 * DiffBlock — renderiza as linhas do diff unificado de um artefato.
 * Colore linhas +/- e hunk headers, exibe número de linha contextual.
 */
function DiffBlock({ diff }) {
  const lines = parseDiff(diff);

  if (lines.length === 0) {
    return (
      <div style={{
        padding:    '12px 16px',
        fontSize:   11,
        color:      C.neutralMuted,
        fontStyle:  'italic',
      }}>
        Diff não disponível para este artefato.
      </div>
    );
  }

  const lineColors = {
    hunk: { bg: `${C.quantum}18`, color: C.quantum },
    add:  { bg: `${C.cyber}14`,   color: C.cyber   },
    del:  { bg: `${C.hot}14`,     color: C.hot     },
    ctx:  { bg: 'transparent',    color: C.neutralDim },
    meta: { bg: 'transparent',    color: C.neutralMuted },
  };

  return (
    <div
      role="region"
      aria-label="Diff do arquivo"
      style={{
        fontFamily:      '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
        fontSize:        11,
        lineHeight:      1.6,
        overflowX:       'auto',
        backgroundColor: '#08090A',
        borderRadius:    6,
        border:          `1px solid ${C.blackBorder}`,
      }}
    >
      {lines.map((ln, i) => {
        const meta = lineColors[ln.type] ?? lineColors.ctx;
        return (
          <div
            key={i}
            style={{
              display:         'flex',
              backgroundColor: meta.bg,
              minHeight:       20,
            }}
          >
            {/* Gutter */}
            <span style={{
              width:       36,
              flexShrink:  0,
              textAlign:   'right',
              paddingRight: 10,
              color:       C.neutralMuted,
              userSelect:  'none',
              fontSize:    10,
              lineHeight:  '20px',
              borderRight: `1px solid ${C.blackBorder}`,
            }}>
              {ln.type !== 'hunk' && ln.type !== 'meta' ? i + 1 : ''}
            </span>
            {/* Conteúdo */}
            <span style={{
              flex:       1,
              paddingLeft: 10,
              paddingRight: 16,
              whiteSpace: 'pre',
              color:      meta.color,
              overflowX:  'auto',
            }}>
              {ln.text}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * ArtifactRow — linha de artefato com badge action e contador de linhas.
 * Expande para mostrar o diff ao clicar.
 */
function ArtifactRow({ artifact, index, total }) {
  const [expanded, setExpanded] = useState(false);
  const actionMeta = ACTION_META[artifact.action] ?? { label: artifact.action, color: C.neutralMuted };

  return (
    <div
      style={{
        borderBottom: index < total - 1 ? `1px solid ${C.blackBorder}` : 'none',
      }}
    >
      {/* Cabeçalho da linha */}
      <button
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Recolher' : 'Expandir'} diff: ${artifact.path}`}
        style={{
          width:           '100%',
          display:         'flex',
          alignItems:      'center',
          gap:             10,
          padding:         '10px 16px',
          background:      'transparent',
          border:          'none',
          cursor:          'pointer',
          textAlign:       'left',
          transition:      'background-color 120ms',
        }}
      >
        {/* Badge de ação A/M/D */}
        <span
          aria-label={actionMeta.label}
          style={{
            width:           20,
            height:          20,
            borderRadius:    3,
            backgroundColor: `${actionMeta.color}22`,
            border:          `1px solid ${actionMeta.color}44`,
            color:           actionMeta.color,
            fontSize:        10,
            fontWeight:      700,
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            flexShrink:      0,
            fontFamily:      'monospace',
          }}
        >
          {artifact.action}
        </span>

        {/* Caminho do arquivo */}
        <span style={{
          flex:         1,
          fontSize:     12,
          color:        C.neutral,
          fontFamily:   'monospace',
          overflow:     'hidden',
          textOverflow: 'ellipsis',
          whiteSpace:   'nowrap',
        }}>
          {artifact.path}
        </span>

        {/* Contador additions/deletions */}
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {artifact.additions > 0 && (
            <span style={{ fontSize: 11, color: C.cyber, fontVariantNumeric: 'tabular-nums' }}>
              +{artifact.additions}
            </span>
          )}
          {artifact.deletions > 0 && (
            <span style={{ fontSize: 11, color: C.hot, fontVariantNumeric: 'tabular-nums' }}>
              −{artifact.deletions}
            </span>
          )}
        </span>

        {/* Badge simulado por artefato */}
        {artifact.is_simulated && (
          <span
            aria-label="Artefato gerado em modo simulado"
            style={{
              fontSize:        9,
              fontWeight:      700,
              padding:         '2px 6px',
              borderRadius:    4,
              backgroundColor: `${C.digital}22`,
              border:          `1px solid ${C.digital}44`,
              color:           C.digital,
              textTransform:   'uppercase',
              letterSpacing:   '0.04em',
              flexShrink:      0,
              whiteSpace:      'nowrap',
            }}
          >
            SIM.
          </span>
        )}

        {/* Chevron expansão */}
        <span style={{ color: C.neutralMuted, flexShrink: 0, lineHeight: 0 }}>
          {expanded
            ? <ChevronLeft size={13} aria-hidden="true" style={{ transform: 'rotate(-90deg)' }} />
            : <ChevronRight size={13} aria-hidden="true" style={{ transform: 'rotate(90deg)' }} />}
        </span>
      </button>

      {/* Diff expandido */}
      {expanded && (
        <div style={{ padding: '0 16px 14px' }}>
          <DiffBlock diff={artifact.diff} />
        </div>
      )}
    </div>
  );
}

/**
 * CodeArtifactsTab — lista paginada de artefatos com diffs reais.
 */
function CodeArtifactsTab({ executionId, initialArtifacts, artifactsTotal }) {
  const [page, setPage] = useState(0);
  const offset    = page * ARTIFACTS_PER_PAGE;
  const isFirstPage = page === 0;

  // Usa dados iniciais (do output) na página 0; a partir da página 1, busca paginado
  const paginatedQuery = useExecutionArtifacts(
    isFirstPage ? null : executionId, // desabilita quando página 0
    ARTIFACTS_PER_PAGE,
    offset,
  );

  const artifacts     = isFirstPage ? (initialArtifacts ?? []) : (paginatedQuery.data?.code_artifacts ?? []);
  const total         = artifactsTotal ?? 0;
  const totalPages    = Math.max(1, Math.ceil(total / ARTIFACTS_PER_PAGE));
  const isLoading     = !isFirstPage && paginatedQuery.isLoading;
  const isError       = !isFirstPage && paginatedQuery.isError;
  const isFetching    = !isFirstPage && paginatedQuery.isFetching;

  if (total === 0 && !initialArtifacts?.length) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: C.neutralMuted, fontSize: 13 }}>
        Nenhum artefato de código disponível.
      </div>
    );
  }

  // Totais globais de additions/deletions (página atual)
  const totalAdditions = artifacts.reduce((s, a) => s + (a.additions ?? 0), 0);
  const totalDeletions = artifacts.reduce((s, a) => s + (a.deletions ?? 0), 0);
  const countCreated   = artifacts.filter((a) => a.action === 'A').length;
  const countModified  = artifacts.filter((a) => a.action === 'M').length;
  const countDeleted   = artifacts.filter((a) => a.action === 'D').length;
  const hasSimulated   = artifacts.some((a) => a.is_simulated);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Resumo ── */}
      <div style={{
        display:         'flex',
        alignItems:      'center',
        gap:             16,
        padding:         '12px 16px',
        borderRadius:    8,
        backgroundColor: C.blackElev,
        border:          `1px solid ${C.blackBorder}`,
        flexWrap:        'wrap',
      }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', flex: 1 }}>
          <span style={{ fontSize: 12, color: C.neutralDim }}>
            <strong style={{ color: C.neutral, fontVariantNumeric: 'tabular-nums' }}>{total}</strong> arquivos
          </span>
          {countCreated > 0 && (
            <span style={{ fontSize: 12, color: C.neutralDim }}>
              <strong style={{ color: C.cyber }}>{countCreated}</strong> criados
            </span>
          )}
          {countModified > 0 && (
            <span style={{ fontSize: 12, color: C.neutralDim }}>
              <strong style={{ color: C.quantum }}>{countModified}</strong> modificados
            </span>
          )}
          {countDeleted > 0 && (
            <span style={{ fontSize: 12, color: C.neutralDim }}>
              <strong style={{ color: C.hot }}>{countDeleted}</strong> deletados
            </span>
          )}
          {totalAdditions > 0 && (
            <span style={{ fontSize: 12, color: C.cyber, fontVariantNumeric: 'tabular-nums' }}>
              +{totalAdditions}
            </span>
          )}
          {totalDeletions > 0 && (
            <span style={{ fontSize: 12, color: C.hot, fontVariantNumeric: 'tabular-nums' }}>
              −{totalDeletions}
            </span>
          )}
        </div>

        {hasSimulated && (
          <span style={{
            fontSize:        10,
            fontWeight:      700,
            padding:         '2px 7px',
            borderRadius:    4,
            backgroundColor: `${C.digital}22`,
            border:          `1px solid ${C.digital}44`,
            color:           C.digital,
            textTransform:   'uppercase',
            letterSpacing:   '0.04em',
          }}>
            Contém artefatos simulados
          </span>
        )}
      </div>

      {/* ── Lista de artefatos ── */}
      {isLoading ? (
        <LoadingSpinner label="Carregando artefatos..." />
      ) : isError ? (
        <QueryError
          error={paginatedQuery.error}
          onRetry={paginatedQuery.refetch}
        />
      ) : (
        <Card padded={false}>
          {artifacts.map((artifact, i) => (
            <ArtifactRow
              key={artifact.artifact_id ?? i}
              artifact={artifact}
              index={i}
              total={artifacts.length}
            />
          ))}
        </Card>
      )}

      {/* ── Paginação ── */}
      {totalPages > 1 && (
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          gap:            12,
        }}>
          <Btn
            variant="ghost"
            size="sm"
            icon={ChevronLeft}
            disabled={page === 0 || isFetching}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label="Página anterior de artefatos"
          >
            Anterior
          </Btn>
          <span style={{
            fontSize:   12,
            color:      C.neutralDim,
            fontVariantNumeric: 'tabular-nums',
            minWidth:   80,
            textAlign:  'center',
          }}>
            {page + 1} / {totalPages}
            {isFetching && <span style={{ marginLeft: 6, color: C.neutralMuted }}>…</span>}
          </span>
          <Btn
            variant="ghost"
            size="sm"
            icon={ChevronRight}
            disabled={page >= totalPages - 1 || isFetching}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            aria-label="Próxima página de artefatos"
          >
            Próxima
          </Btn>
        </div>
      )}
    </div>
  );
}

// ─── ComplianceTab ───────────────────────────────────────────────────────────

/**
 * ComplianceTab — aba Compliance Check com 11 (ou mais) checks.
 */
/** Linha de um compliance check + controle de WAIVER granular (Gate B). */
function ComplianceCheckRow({ check, executionId, last }) {
  const meta = COMPLIANCE_STATUS_META[check.result] ?? COMPLIANCE_STATUS_META.warning;
  const Icon = meta.Icon;
  const waive = useWaiveComplianceCheck(executionId);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const editable = Boolean(executionId);
  const checkedAt = check.checked_at
    ? new Date(check.checked_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px',
      borderBottom: last ? 'none' : `1px solid ${C.blackBorder}` }}>
      <Icon size={14} aria-hidden="true" style={{ color: meta.color, flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.neutral }}>{check.standard}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
            backgroundColor: `${meta.color}22`, border: `1px solid ${meta.color}44`, color: meta.color,
            textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{meta.label}</span>
          {check.has_waiver && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
              backgroundColor: `${C.warn}22`, border: `1px solid ${C.warn}66`, color: C.warn,
              textTransform: 'uppercase' }}>waiver</span>
          )}
        </div>
        {check.evidence && (
          <div style={{ fontSize: 12, color: C.neutralDim, marginTop: 4, lineHeight: 1.5 }}>{check.evidence}</div>
        )}

        {/* Waiver granular: aceitar/retirar risco por check (não o gate inteiro) */}
        {editable && (
          <div style={{ marginTop: 8 }}>
            {check.has_waiver ? (
              <button onClick={() => waive.mutate({ checkId: check.check_id, waiver: false })}
                disabled={waive.isPending}
                style={{ cursor: 'pointer', background: 'transparent', color: C.neutralDim,
                  border: `1px solid ${C.blackBorder}`, borderRadius: 6, padding: '4px 10px', fontSize: 11.5 }}>
                {waive.isPending ? '...' : 'Retirar waiver'}
              </button>
            ) : !open ? (
              <button onClick={() => setOpen(true)} disabled={waive.isPending}
                style={{ cursor: 'pointer', background: 'transparent', color: C.warn,
                  border: `1px solid ${C.warn}66`, borderRadius: 6, padding: '4px 10px', fontSize: 11.5, fontWeight: 600 }}>
                ⚠ Waiver (aceitar risco)
              </button>
            ) : (
              <div>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Justificativa do waiver (obrigatória) — risco aceito e por quê."
                  rows={2} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', background: C.blackElev2,
                    color: C.neutral, border: `1px solid ${C.blackBorder}`, borderRadius: 6, padding: 8, fontSize: 12.5 }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <button onClick={() => reason.trim() && waive.mutate(
                      { checkId: check.check_id, waiver: true, waiver_reason: reason.trim() },
                      { onSuccess: () => { setOpen(false); setReason(''); } })}
                    disabled={!reason.trim() || waive.isPending}
                    style={{ cursor: reason.trim() ? 'pointer' : 'default', background: C.warn, color: C.black,
                      border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700,
                      opacity: reason.trim() ? 1 : 0.5 }}>
                    {waive.isPending ? 'Salvando…' : 'Confirmar waiver'}
                  </button>
                  <button onClick={() => { setOpen(false); setReason(''); }} disabled={waive.isPending}
                    style={{ cursor: 'pointer', background: 'transparent', color: C.neutralDim,
                      border: `1px solid ${C.blackBorder}`, borderRadius: 6, padding: '5px 12px', fontSize: 12 }}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
            {waive.isError && <div style={{ fontSize: 11.5, color: C.hot, marginTop: 6 }}>Falha ao salvar o waiver.</div>}
          </div>
        )}
      </div>
      {checkedAt && (
        <span style={{ fontSize: 11, color: C.neutralMuted, flexShrink: 0, marginTop: 1 }}>{checkedAt}</span>
      )}
    </div>
  );
}

function ComplianceTab({ checks, executionId }) {
  if (!checks || checks.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: C.neutralMuted, fontSize: 13 }}>
        Nenhum check de compliance disponível.
      </div>
    );
  }

  // API (ComplianceCheckOut): standard, result (passed|failed|warning|skipped),
  // evidence, has_waiver, checked_at.
  const passed  = checks.filter((c) => c.result === 'passed').length;
  const failed  = checks.filter((c) => c.result === 'failed').length;
  const total   = checks.length;
  const allPass = failed === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Resumo */}
      <div style={{
        display:         'flex',
        alignItems:      'center',
        gap:             12,
        padding:         '14px 18px',
        borderRadius:    8,
        backgroundColor: allPass ? `${C.cyber}11` : `${C.hot}11`,
        border:          `1px solid ${allPass ? C.cyber : C.hot}44`,
      }}>
        {allPass
          ? <CheckCircle size={18} aria-hidden="true" style={{ color: C.cyber }} />
          : <XCircle     size={18} aria-hidden="true" style={{ color: C.hot  }} />}
        <div>
          <div style={{
            fontSize:      13,
            fontWeight:    700,
            color:         allPass ? C.cyber : C.hot,
            letterSpacing: '0.02em',
          }}>
            {passed}/{total} standards aprovados · {allPass ? 'APROVADO' : `${failed} REPROVADO${failed > 1 ? 'S' : ''}`}
          </div>
        </div>
      </div>

      {/* Lista de checks — cada um com waiver granular */}
      <Card padded={false}>
        {checks.map((check, i) => (
          <ComplianceCheckRow
            key={check.check_id ?? i}
            check={check}
            executionId={executionId}
            last={i === checks.length - 1}
          />
        ))}
      </Card>
    </div>
  );
}

// ─── EventList ────────────────────────────────────────────────────────────────

/**
 * EventList — lista de eventos de auditoria ou replay.
 */
function EventList({ events, emptyMsg = 'Nenhum evento registrado.' }) {
  if (!events || events.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: C.neutralMuted, fontSize: 13 }}>
        {emptyMsg}
      </div>
    );
  }

  return (
    <Card padded={false}>
      {events.map((ev, i) => {
        const ts = ev.occurred_at
          ? new Date(ev.occurred_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
          : null;

        return (
          <div
            key={ev.seq ?? i}
            style={{
              display:      'flex',
              alignItems:   'flex-start',
              gap:          12,
              padding:      '10px 16px',
              borderBottom: i < events.length - 1 ? `1px solid ${C.blackBorder}` : 'none',
            }}
          >
            {/* Número de sequência */}
            <span style={{
              fontSize:    10,
              fontWeight:  700,
              color:       C.neutralMuted,
              minWidth:    28,
              fontFamily:  'monospace',
              flexShrink:  0,
              marginTop:   2,
            }}>
              {ev.seq ?? i + 1}
            </span>

            {/* Tipo de evento */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.neutral }}>
                {ev.event_type}
              </div>
              {ev.actor_type && (
                <div style={{ fontSize: 11, color: C.cyber, marginTop: 2 }}>
                  {ev.actor_type}
                </div>
              )}
            </div>

            {ts && (
              <span style={{ fontSize: 11, color: C.neutralMuted, flexShrink: 0 }}>
                {ts}
              </span>
            )}
          </div>
        );
      })}
    </Card>
  );
}

// ─── Export (download client-side) ─────────────────────────────────────────────

function _download(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportJson(filename, data) {
  _download(filename, JSON.stringify(data, null, 2), 'application/json');
}

function exportCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => esc(r[h])).join(',')),
  ].join('\n');
  _download(filename, csv, 'text/csv;charset=utf-8');
}

// ─── AuditTab ────────────────────────────────────────────────────────────────

/**
 * AuditTab — aba Audit Report.
 */
function AuditTab({ events, executionId }) {
  const suffix = executionId?.slice(0, 8) ?? 'exec';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'space-between',
        flexWrap:     'wrap',
        gap:          8,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.neutral }}>
            Trilha de Auditoria
          </div>
          <div style={{ fontSize: 12, color: C.neutralMuted }}>
            Execução #{executionId?.slice(0, 12) ?? '—'} · {events?.length ?? 0} eventos
          </div>
        </div>
        <Btn
          variant="ghost"
          size="sm"
          icon={Download}
          disabled={!events?.length}
          onClick={() => exportCsv(`auditoria-${suffix}.csv`, events)}
        >
          Exportar CSV
        </Btn>
      </div>

      <EventList events={events} emptyMsg="Nenhum evento de auditoria registrado." />
    </div>
  );
}

// ─── ReplayTab ────────────────────────────────────────────────────────────────

/**
 * ReplayTab — aba Replay Timeline.
 */
function ReplayTab({ events, executionId }) {
  const suffix = executionId?.slice(0, 8) ?? 'exec';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'space-between',
        flexWrap:     'wrap',
        gap:          8,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.neutral }}>
            Replay Determinístico
          </div>
          <div style={{ fontSize: 12, color: C.neutralMuted }}>
            {events?.length ?? 0} eventos · 100% determinístico
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="outline" size="sm" icon={Play} disabled>
            Reproduzir
          </Btn>
          <Btn
            variant="ghost"
            size="sm"
            icon={Download}
            disabled={!events?.length}
            onClick={() => exportJson(`replay-${suffix}.json`, events)}
          >
            Exportar JSON
          </Btn>
        </div>
      </div>

      <EventList events={events} emptyMsg="Nenhum evento de replay registrado." />
    </div>
  );
}

// ─── AnalysisTab — aba Análise (delivery_scope === 'analise') ────────────────

/**
 * AnalysisArtifact — accordion expansível de um artefato de análise.
 * Reusa o padrão de ArtifactsScreen (RichContent para renderizar markdown/mermaid).
 */
function AnalysisArtifact({ artifact, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{
      border:       `1px solid ${C.blackBorder}`,
      background:   C.blackElev,
      borderRadius: 10,
      overflow:     'hidden',
    }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`${open ? 'Recolher' : 'Expandir'} análise: ${artifact.title}`}
        style={{
          width:      '100%',
          display:    'flex',
          alignItems: 'center',
          gap:        10,
          padding:    '12px 16px',
          background: 'transparent',
          border:     'none',
          cursor:     'pointer',
          textAlign:  'left',
        }}
      >
        <span style={{ color: C.cyber, fontFamily: 'monospace', fontSize: 14, flexShrink: 0 }}>
          {open ? '–' : '+'}
        </span>
        {artifact.kind && (
          <span style={{
            fontFamily:      'monospace',
            fontSize:        10,
            padding:         '2px 8px',
            borderRadius:    6,
            backgroundColor: `${C.quantum}22`,
            border:          `1px solid ${C.quantum}44`,
            color:           C.quantum,
            textTransform:   'uppercase',
            letterSpacing:   '0.04em',
            flexShrink:      0,
            whiteSpace:      'nowrap',
          }}>
            {artifact.kind}
          </span>
        )}
        <strong style={{ fontSize: 13.5, color: C.neutral }}>{artifact.title}</strong>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); downloadFile(`${slugify(artifact.title || artifact.kind)}.md`, artifact.content || '', 'text/markdown'); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); downloadFile(`${slugify(artifact.title || artifact.kind)}.md`, artifact.content || '', 'text/markdown'); } }}
          title="Baixar este artefato (.md)"
          style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 11, color: C.cyber, cursor: 'pointer',
            border: `1px solid ${C.cyber}44`, borderRadius: 6, padding: '2px 8px' }}
        >
          ↓ .md
        </span>
      </button>
      {open && (
        <div style={{
          padding:    '12px 18px',
          borderTop:  `1px solid ${C.blackBorder}`,
          background: C.black,
        }}>
          <RichContent content={artifact.content} />
        </div>
      )}
    </div>
  );
}

/**
 * AnalysisTab — lista os resultados da IA (analysis_artifacts) como accordions.
 * Empty-state pt-BR quando a análise ainda não foi consolidada.
 */
function AnalysisTab({ artifacts, executionId, projectName }) {
  const genRec = useGenerateRecommendations(executionId);
  const genReport = useGenerateReport(executionId);
  const [htmlBusy, setHtmlBusy] = useState(false);

  if (!artifacts || artifacts.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: C.neutralMuted, fontSize: 13, lineHeight: 1.6 }}>
        A análise ainda está sendo consolidada. Assim que a IA concluir, os
        resultados aparecerão aqui.
      </div>
    );
  }

  const name = projectName || 'Análise';
  const dlMd = () => downloadFile(`${slugify(name)}-analise.md`, artifactsToMarkdown(artifacts, name), 'text/markdown');
  const dlHtml = async () => {
    try {
      setHtmlBusy(true);
      const html = await buildAnalysisHtml(artifacts, name);
      downloadFile(`${slugify(name)}-analise.html`, html, 'text/html');
    } finally { setHtmlBusy(false); }
  };
  const genRecommendations = () => genRec.mutate();

  const btn = (extra = {}) => ({
    fontSize: 12.5, fontWeight: 600, padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
    background: 'transparent', color: C.cyber, border: `1px solid ${C.cyber}55`, ...extra,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Toolbar de entregáveis */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
        padding: '10px 12px', border: `1px solid ${C.blackBorder}`, borderRadius: 10, background: C.blackElev }}>
        <span style={{ fontSize: 12, color: C.neutralDim, marginRight: 4 }}>Entregáveis:</span>
        <button type="button" onClick={dlMd} style={btn()}>↓ Baixar tudo (.md)</button>
        <button type="button" onClick={dlHtml} disabled={htmlBusy} style={btn({ opacity: htmlBusy ? 0.6 : 1 })}>
          {htmlBusy ? 'Gerando HTML…' : '↓ HTML navegável'}
        </button>
        <button type="button" onClick={() => genReport.mutate()} disabled={genReport.isPending}
          title="(Re)gera o Relatório de Análise completo — resultados de todas as áreas"
          style={btn({ opacity: genReport.isPending ? 0.6 : 1 })}>
          {genReport.isPending ? 'Consolidando…' : '↻ Regerar Relatório completo'}
        </button>
        <button type="button" onClick={genRecommendations} disabled={genRec.isPending}
          style={btn({ marginLeft: 'auto', color: C.black, background: C.cyber, border: 'none',
            opacity: genRec.isPending ? 0.6 : 1 })}>
          {genRec.isPending ? 'Gerando…' : '✨ Gerar Recomendações & Mitigações'}
        </button>
      </div>
      <AiProgress
        busy={genReport.isPending || genRec.isPending}
        label={genRec.isPending ? 'Gerando recomendações' : 'Consolidando o relatório'}
      />

      {(genRec.isError || genReport.isError) && (
        <div style={{ fontSize: 12.5, color: C.hot }}>
          {String((genReport.error || genRec.error)?.title || (genReport.error || genRec.error)?.message || genReport.error || genRec.error)}
        </div>
      )}
      {genRec.data?.content && (
        <div style={{ border: `1px solid ${C.cyber}44`, borderRadius: 10, background: C.blackElev, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <strong style={{ fontSize: 13.5, color: C.neutral }}>Recomendações & Mitigações (pronto p/ subir como novo projeto)</strong>
            <button type="button"
              onClick={() => downloadFile(`${slugify(name)}-recomendacoes.md`, genRec.data.content, 'text/markdown')}
              style={{ marginLeft: 'auto', ...btn() }}>↓ .md</button>
          </div>
          <AiTruncationNotice complete={genRec.data?.aiComplete} onRetry={genRecommendations} />
          <RichContent content={genRec.data.content} />
        </div>
      )}

      <AiTruncationNotice complete={genReport.data?.aiComplete} onRetry={() => genReport.mutate()} />
      {artifacts.map((artifact, i) => (
        <AnalysisArtifact key={i} artifact={artifact} defaultOpen={i === 0} />
      ))}
    </div>
  );
}

// ─── Tela principal ───────────────────────────────────────────────────────────

/**
 * OutputScreen — resultado da execução com 5 abas.
 * Lê executionId de ?execution=<id>.
 */
// ─── DeliveryBanner — "Enviar ao GitHub" (aprovação humana da entrega verificada) ──
// Aparece só quando há entrega VERIFICADA no sandbox aguardando o OK do dono
// (pending_deliveries). Verde no sandbox NÃO empurra sozinho — "só vai pro GitHub
// quando eu falo que está tudo ok". Botão habilita no verde; após enviar, mostra os PRs.
function DeliveryBanner({ executionId }) {
  const [sending, setSending] = useState(false);
  const { data } = useDeliveryStatus(executionId, { refetchInterval: sending ? 3000 : false });
  const push = usePushToGithub();
  const baixarPendencias = useBaixarCorrecoesPendentes();
  const baixarComplementares = useBaixarAtividadesComplementares();
  const baixarSetupIa = useBaixarSetupIaAmbiente();
  const baixarProjeto = useBaixarProjetoDaEntrega();
  const [baixandoProjeto, setBaixandoProjeto] = useState(false);
  const [erroPendencias, setErroPendencias] = useState(null);

  const repos    = data?.repos ?? [];
  const awaiting = data?.awaiting_approval ?? 0;
  const pushed   = data?.pushed ?? 0;
  const failed   = data?.failed ?? 0;
  // MODO ESTRITO (Onda 5, D1): o envio só é liberado com todos os repositórios aguardando
  // envio em READY_FOR_REVIEW — a API recusa (409) de qualquer jeito; aqui o dono vê o porquê.
  // Com a garantia DESLIGADA na API, o strict não bloqueia — a tela segue a API (A7).
  const strict = data?.contract_mode === 'strict' && data?.assurance_enabled !== false;
  const naoProntos = repos.filter((r) => r.status === 'awaiting_approval'
                                        && r.assurance_status !== 'READY_FOR_REVIEW');
  const bloqueadoPeloStrict = strict && naoProntos.length > 0;
  const semEstado = naoProntos.filter((r) => !r.assurance_status);
  // Modo de entrega `client`: a plataforma não rodou o projeto — nada de "sandbox verde".
  // Quem diz o modo é a entrega (`delivery_mode` da API): com pendência o estado vem
  // HUMAN_REVIEW_REQUIRED e só o modo conta que nada rodou. Resposta sem o campo → o estado.
  const entregaNoCliente = data?.delivery_mode === 'client'
    || repos.some((r) => r.assurance_status === 'READY_FOR_CLIENT_FINALIZATION');
  const comPendencia = repos.some((r) => r.assurance_status === 'HUMAN_REVIEW_REQUIRED');

  useEffect(() => {
    if (sending && awaiting === 0 && (pushed > 0 || failed > 0)) setSending(false);
  }, [sending, awaiting, pushed, failed]);

  if (!repos.length) return null;  // nada pendente (execução antiga / sem verificação)

  const onSend = () => {
    setSending(true);
    push.mutate(executionId, { onError: () => setSending(false) });
  };
  const onBaixarProjeto = () => {
    setErroPendencias(null);
    setBaixandoProjeto(true);
    baixarProjeto(executionId)
      .catch((e) => setErroPendencias(e?.detail || 'Não foi possível baixar o projeto.'))
      .finally(() => setBaixandoProjeto(false));
  };
  const busy    = sending || push.isPending;
  const allSent = awaiting === 0 && pushed > 0 && failed === 0;
  const border  = failed > 0 ? C.hot : (allSent ? C.cyber : C.blackBorder);

  return (
    <Card style={{ marginBottom: 20, padding: 16, borderColor: border, borderWidth: 1, borderStyle: 'solid' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <GitPullRequest size={16} aria-hidden="true" style={{ color: border }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: C.neutral }}>
              {allSent ? 'Entrega enviada ao GitHub' : (entregaNoCliente
                ? (comPendencia ? 'Entrega para finalização no cliente — com pendências' : 'Entrega pronta para finalização no cliente')
                : 'Entrega verificada no sandbox')}
            </span>
            <Badge color={failed > 0 ? C.hot : C.cyber} light>
              {allSent ? 'PR ABERTO' : (failed > 0 ? 'FALHA NO ENVIO' : (entregaNoCliente ? 'FINALIZAÇÃO NO CLIENTE' : '✅ SANDBOX VERDE'))}
            </Badge>
          </div>
          <div style={{ fontSize: 12, color: C.neutralMuted }}>
            {allSent
              ? (entregaNoCliente
                  ? `${pushed} repositório(s) enviado(s). ${AJUDA_FINALIZACAO_NO_CLIENTE}`
                  : `${pushed} repositório(s) enviado(s) — o CI já entra verde (build+test passaram no sandbox).`)
              : (awaiting > 0
                  ? (entregaNoCliente
                      ? `${awaiting} repositório(s) gerado(s) e conferido(s) pela plataforma aguardam seu OK. Nada foi pro GitHub ainda.`
                      : `${awaiting} repositório(s) passaram 100% no sandbox (build+test) e aguardam seu OK. Nada foi pro GitHub ainda.`)
                  : `${failed} repositório(s) falharam no envio — veja abaixo.`)}
          </div>
          {entregaNoCliente && (
            <div data-testid="kit-de-finalizacao" style={{ marginTop: 4, fontSize: 12, color: C.digital }}>
              A entrega leva o kit de finalização: abra o projeto no Claude Code e digite{' '}
              <code>/finalizar</code>, começando pelo <code>FINALIZAR.md</code> da raiz (ele vem no .zip do projeto).
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Baixar em vez de enviar (pedido do dono, 2026-10-06): mesmo conteúdo do envio
              ao GitHub, para testar na própria máquina. Vale antes e depois do envio. */}
          <Btn variant="secondary" size="sm" onClick={onBaixarProjeto} disabled={baixandoProjeto}
               aria-label="Baixar o projeto da entrega em um arquivo .zip"
               title="Mesmo conteúdo que seria enviado ao GitHub. Em projeto sobre repositório existente, traz só os arquivos criados ou alterados.">
            {baixandoProjeto ? 'Preparando o .zip…' : '⬇ Baixar projeto (.zip)'}
          </Btn>
          {awaiting > 0 && (
            <Btn variant="primary" size="sm" onClick={onSend} disabled={busy || bloqueadoPeloStrict}
                 aria-label={entregaNoCliente
                   ? 'Enviar ao GitHub a entrega para finalização no cliente'
                   : 'Enviar ao GitHub a entrega verificada'}>
              {busy ? 'Enviando…' : `Enviar ao GitHub (${awaiting})`}
            </Btn>
          )}
        </div>
      </div>
      {bloqueadoPeloStrict && (
        <div role="status" style={{ marginTop: 8, fontSize: 12, color: C.warn }}>
          Modo estrito: o envio só é liberado com a entrega pronta para revisão —
          {' '}{naoProntos.filter((r) => r.assurance_status).map((r) => r.repo_full_name).join(', ')}
          {naoProntos.some((r) => r.assurance_status) ? ' exige(m) revisão humana' : ''}
          {semEstado.length > 0 && `${naoProntos.some((r) => r.assurance_status) ? '; ' : ''}sem estado calculado: ${semEstado.map((r) => r.repo_full_name).join(', ')}`}.
        </div>
      )}

      {/* status por repositório */}
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {repos.map((r) => {
          // "VERIFICAÇÃO PARCIAL" = parte do código entregue não foi compilada (pasta ou
          // stack fora do plano). Não pode aparecer como verde cheio.
          const parcial = (r.verify_summary || '').startsWith('VERIFICAÇÃO PARCIAL');
          return (
            <div key={r.repo_full_name} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                fontSize: 12, color: C.neutralMuted, flexWrap: 'wrap',
              }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: 'monospace' }}>{r.repo_full_name}</span>
                  <SeloDeGarantia estado={r.assurance_status} compacto />
                </span>
                {r.status === 'pushed' && r.pr_url ? (
                  <a href={r.pr_url} target="_blank" rel="noopener noreferrer"
                     style={{ color: C.cyber, fontWeight: 600 }}>Ver Pull Request ↗</a>
                ) : r.status === 'failed' ? (
                  <span style={{ color: C.hot }} title={r.error || ''}>falhou no envio</span>
                ) : entregaNoCliente ? (
                  <span style={{ color: C.digital }}>{AJUDA_FINALIZACAO_NO_CLIENTE}</span>
                ) : parcial ? (
                  <span style={{ color: C.warn }}>⚠️ verificação parcial — pronto p/ enviar</span>
                ) : (
                  <span style={{ color: C.cyber }}>✅ verde — pronto p/ enviar</span>
                )}
              </div>
              {r.assurance_status === 'HUMAN_REVIEW_REQUIRED' && (r.assurance_motivos ?? []).length > 0 && (
                <ul data-testid="motivos-garantia"
                    style={{ margin: '2px 0 0 18px', padding: 0, fontSize: 11, color: C.warn }}>
                  {r.assurance_motivos.map((m) => <li key={m}>{m}</li>)}
                </ul>
              )}
              {r.verify_summary && (
                <div data-testid="verify-summary" style={{ fontSize: 11, color: parcial ? C.warn : C.neutralDim }}>
                  {r.verify_summary}
                </div>
              )}
              {/* O roteiro vem primeiro: diz o que colar no agente de IA e em que ordem
                  (ambiente → correções → complementares → massa de testes → telas × backend →
                  padrão visual e navegação → conferência). */}
              {r.setup_ia_ambiente && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                  <Btn variant="secondary" size="sm"
                       onClick={() => {
                         setErroPendencias(null);
                         baixarSetupIa(executionId, r.repo_full_name)
                           .catch((e) => setErroPendencias(e?.detail || 'Não foi possível baixar o arquivo.'));
                       }}
                       aria-label={`Baixar o roteiro para o agente de IA de ${r.repo_full_name}`}>
                    🧭 Baixar o roteiro para a IA (.md)
                  </Btn>
                  {/* Modo `client`: o começo é o FINALIZAR.md do kit, não este roteiro. */}
                  {!entregaNoCliente && (
                    <span style={{ fontSize: 11, color: C.neutralDim }}>
                      Comece por ele: o que colar no Claude Code, Codex ou outra IA, e em que ordem, para finalizar o projeto.
                    </span>
                  )}
                </div>
              )}
              {/* O que não fechou depois dos reforços vai num .md que um agente de IA
                  (Claude Code, Cursor, Copilot) do cliente consegue executar. */}
              {r.correcoes_pendentes && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                  <Btn variant="secondary" size="sm"
                       onClick={() => {
                         setErroPendencias(null);
                         baixarPendencias(executionId, r.repo_full_name)
                           .catch((e) => setErroPendencias(e?.detail || 'Não foi possível baixar o arquivo.'));
                       }}
                       aria-label={`Baixar correções pendentes de ${r.repo_full_name}`}>
                    📝 Baixar correções pendentes (.md)
                  </Btn>
                  <span style={{ fontSize: 11, color: C.warn }}>
                    O que não fechou depois dos reforços — entregue a um agente de IA para finalizar.
                  </span>
                </div>
              )}
              {/* Atividades complementares NÃO são erros: o que uma etapa precisou de outra
                  camada (tabela, endpoint, teste de obrigação) e documentou. Visual neutro. */}
              {r.atividades_complementares && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                  <Btn variant="secondary" size="sm"
                       onClick={() => {
                         setErroPendencias(null);
                         baixarComplementares(executionId, r.repo_full_name)
                           .catch((e) => setErroPendencias(e?.detail || 'Não foi possível baixar o arquivo.'));
                       }}
                       aria-label={`Baixar atividades complementares de ${r.repo_full_name}`}>
                    📋 Baixar atividades complementares (.md)
                  </Btn>
                  <span style={{ fontSize: 11, color: C.neutralDim }}>
                    Não são erros: o que uma etapa identificou que falta de outra camada — rode com um agente de IA.
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {erroPendencias && (
        <div role="alert" style={{ marginTop: 8, fontSize: 12, color: C.hot }}>{erroPendencias}</div>
      )}
      {push.isError && (
        <div style={{ marginTop: 8, fontSize: 12, color: C.hot }}>
          Não foi possível iniciar o envio. Tente novamente.
        </div>
      )}
    </Card>
  );
}

export function OutputScreen() {
  const [searchParams] = useSearchParams();
  const executionId = searchParams.get('execution');
  const { navigate } = useNavigationContext();
  const [activeTab, setActiveTab] = useState('pr');

  const {
    data: output,
    isLoading,
    error,
    refetch,
  } = useExecutionOutput(executionId);
  // Projeto da execução — para navegar à Conferência de Entrega com o ?project= certo.
  const { data: execution } = useExecution(executionId);
  const projectId = execution?.project_id ?? null;
  const goDeliveryCheck = () => navigate(
    projectId ? `/execution/delivery-check?project=${projectId}` : 'deliveryCheck',
  );

  // ── Abas derivadas do delivery_scope ────────────────────────────────────────
  // Modo análise ('analise') → "Análise" (+ "Auditoria" só se houver eventos;
  // esconde PR/Código/Compliance/Replay). Caso contrário mantém as 5 abas.
  const isAnalysis = output?.delivery_scope === 'analise';
  const hasAudit = (output?.audit_events?.length ?? 0) > 0;
  const tabs = useMemo(
    () => (isAnalysis ? analysisTabs(hasAudit) : TABS),
    [isAnalysis, hasAudit],
  );

  // Se a aba ativa não existe no conjunto atual (ex.: 'pr' no modo análise),
  // faz fallback para a primeira aba do modo (default 'analise').
  const effectiveTab = tabs.some((t) => t.id === activeTab) ? activeTab : tabs[0].id;

  const handleTabKey = useCallback((e, tabId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActiveTab(tabId);
    }
    // Navegação por setas entre abas
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const idx = tabs.findIndex((t) => t.id === tabId);
      const next = e.key === 'ArrowRight'
        ? (idx + 1) % tabs.length
        : (idx - 1 + tabs.length) % tabs.length;
      setActiveTab(tabs[next].id);
      document.getElementById(`tab-${tabs[next].id}`)?.focus();
    }
  }, [tabs]);

  if (!executionId) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{ fontSize: 14, color: C.neutralMuted, textAlign: 'center' }}>
          Nenhuma execução selecionada.
        </div>
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <Btn variant="ghost" size="sm" onClick={() => navigate('dashboard')}>
            Ir ao Dashboard
          </Btn>
        </div>
      </div>
    );
  }

  if (isLoading) return <LoadingSpinner label="Carregando output..." />;

  if (error) {
    if (error.isForbidden) {
      return (
        <div style={{ padding: 24 }}>
          <ForbiddenBanner detail={error.detail} />
        </div>
      );
    }
    return (
      <div style={{ padding: 24 }}>
        <QueryError error={error} onRetry={refetch} />
      </div>
    );
  }

  if (output === null) {
    return (
      <div style={{
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'center',
        gap:           12,
        padding:       48,
        textAlign:     'center',
      }}>
        <Clock size={40} aria-hidden="true" style={{ color: C.blackBorder }} />
        <div style={{ fontSize: 15, fontWeight: 600, color: C.neutralMuted }}>
          Output não disponível ainda
        </div>
        <div style={{ fontSize: 13, color: C.neutralMuted, maxWidth: 360 }}>
          O output é gerado após a conclusão da execução e aprovação de todos os Quality Gates.
        </div>
        <Btn variant="ghost" size="sm" onClick={() => navigate('liveboard')}>
          Acompanhar execução
        </Btn>
      </div>
    );
  }

  // ── Checar se há falhas de compliance ──────────────────────────────────────
  const hasComplianceFail = (output.compliance_checks ?? []).some((c) => c.result === 'failed');
  const artifactsTotal    = output.artifacts_total ?? (output.code_artifacts?.length ?? 0);

  // ── Métricas reais para o KPI strip do header (igual ao protótipo) ──────────
  const _cc       = output.compliance_checks ?? [];
  const stdPass   = _cc.filter((c) => c.result === 'passed' || c.has_waiver).length;
  const _prs      = (output.pull_requests || []);
  const _multi    = _prs.length > 1;
  const _pr       = output.pull_request ?? _prs[0] ?? null;   // âncora (single ou 1º de N)
  const _arts     = output.code_artifacts ?? [];
  const totalAdds = _multi
    ? _prs.reduce((a, p) => a + (p.additions || 0), 0)
    : (_pr?.additions ?? _arts.reduce((a, x) => a + (x.additions || 0), 0));
  const totalDels = _multi
    ? _prs.reduce((a, p) => a + (p.deletions || 0), 0)
    : (_pr?.deletions ?? _arts.reduce((a, x) => a + (x.deletions || 0), 0));
  const prLabel   = _multi
    ? `${_prs.length} PRs`
    : (_pr ? `#${_pr.external_pr_id || _pr.number || '—'}` : '—');
  const openGitHub = () => { if (_pr?.pr_url) window.open(_pr.pr_url, '_blank', 'noopener'); };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px' }}>

      {/* ── Enviar ao GitHub (aprovação humana da entrega verificada) ────────── */}
      <DeliveryBanner executionId={executionId} />

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'space-between',
        marginBottom: 24,
        flexWrap:     'wrap',
        gap:          12,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: C.neutral, margin: 0 }}>
              Output da Execução
            </h1>
            <Badge color={hasComplianceFail ? C.hot : C.cyber} light>
              {hasComplianceFail ? 'ATENÇÃO' : 'SUCESSO'}
            </Badge>
          </div>
          <div style={{ fontSize: 12, color: C.neutralMuted }}>
            #{executionId.slice(0, 12)}
            {_multi ? (
              <span style={{ marginLeft: 8, color: C.cyber }}>
                · {_prs.length} PRs (1 por repositório)
              </span>
            ) : output.pull_request?.pr_id && (
              <span style={{ marginLeft: 8, color: C.cyber }}>
                · PR {output.pull_request.external_pr_id
                  ? `#${output.pull_request.external_pr_id}`
                  : output.pull_request.pr_id}
              </span>
            )}
            {(_multi ? _prs.some((p) => !p.is_simulated) : (output.pull_request && !output.pull_request.is_simulated)) && (
              <span style={{
                marginLeft:      8,
                fontSize:        10,
                fontWeight:      700,
                padding:         '2px 6px',
                borderRadius:    4,
                backgroundColor: `${C.cyber}22`,
                border:          `1px solid ${C.cyber}44`,
                color:           C.cyber,
                textTransform:   'uppercase',
                letterSpacing:   '0.04em',
              }}>
                PR REAL
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn variant="ghost" size="sm" icon={CheckCircle} onClick={goDeliveryCheck}>
            Conferência de Entrega
          </Btn>
          {_pr?.pr_url && (
            <Btn variant="primary" size="sm" icon={GitPullRequest} onClick={openGitHub}>
              Abrir no GitHub
            </Btn>
          )}
          {hasComplianceFail && (
            <Btn variant="danger" size="sm" icon={XCircle} onClick={() => navigate('failure')}>
              Ver Falhas
            </Btn>
          )}
        </div>
      </div>

      {/* ── KPI strip (métricas reais da execução) ─────────────────────────── */}
      {!isAnalysis && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 10, marginBottom: 20,
        }}>
          {[
            { label: 'Arquivos alterados', value: String(artifactsTotal || 0), sub: `+${totalAdds} · −${totalDels}`, color: C.cyber },
            { label: 'Linhas adicionadas', value: `+${totalAdds}`, sub: `−${totalDels} removidas`, color: C.cyber },
            { label: 'Engineering Standards', value: _cc.length ? `${stdPass}/${_cc.length}` : '—', sub: hasComplianceFail ? 'com reprovação' : 'aprovados', color: hasComplianceFail ? C.hot : C.cyber },
            { label: 'Pull Request', value: prLabel, sub: _multi ? '1 por repositório' : (_pr ? (_pr.is_simulated ? 'simulado' : 'real') : 'sem PR'), color: C.cyber },
          ].filter((k) => MOSTRAR_ENGINEERING_STANDARDS || k.label !== 'Engineering Standards').map((k) => (
            <div key={k.label} style={{
              border: `1px solid ${C.blackBorder}`, background: C.blackElev, borderRadius: 12, padding: 14,
            }}>
              <div style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.neutralMuted }}>{k.label}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: k.color, marginTop: 4, lineHeight: 1 }}>{k.value}</div>
              <div style={{ fontSize: 11, color: C.neutralDim, marginTop: 6 }}>{k.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── A5 FR-17: quem fez o quê e o que é determinístico (relatório de garantia) ── */}
      <QuemFezDaExecucao executionId={executionId} />

      {/* ── Tab Bar ─────────────────────────────────────────────────────────── */}
      <div
        role="tablist"
        aria-label="Abas do Output"
        style={{
          display:      'flex',
          borderBottom: `2px solid ${C.blackBorder}`,
          marginBottom: 24,
          gap:          0,
          overflowX:    'auto',
        }}
      >
        {tabs.map((tab) => {
          const isActive = effectiveTab === tab.id;
          const Icon = tab.Icon;

          // Badge de alerta para a aba Compliance se houver falha
          const hasBadge = tab.id === 'compliance' && hasComplianceFail;

          return (
            <button
              key={tab.id}
              id={`tab-${tab.id}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(e) => handleTabKey(e, tab.id)}
              style={{
                display:         'flex',
                alignItems:      'center',
                gap:             6,
                padding:         '10px 18px',
                fontSize:        13,
                fontWeight:      isActive ? 600 : 400,
                color:           isActive ? C.cyber : C.neutralMuted,
                backgroundColor: 'transparent',
                border:          'none',
                borderBottom:    isActive ? `2px solid ${C.cyber}` : '2px solid transparent',
                cursor:          'pointer',
                transition:      'color 150ms, border-color 150ms',
                whiteSpace:      'nowrap',
                position:        'relative',
                marginBottom:    -2, // sobrepõe a borda do tablist
              }}
            >
              <Icon size={13} aria-hidden="true" />
              {tab.label}
              {hasBadge && (
                <span style={{
                  fontSize:        9,
                  fontWeight:      700,
                  padding:         '1px 5px',
                  borderRadius:    10,
                  backgroundColor: C.hot,
                  color:           C.neutral,
                  marginLeft:      2,
                }}>
                  !
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Tab Panels ──────────────────────────────────────────────────────── */}
      {isAnalysis ? (
        <div
          id="tabpanel-analise"
          role="tabpanel"
          aria-labelledby="tab-analise"
          style={{ display: effectiveTab === 'analise' ? 'block' : 'none' }}
        >
          <AnalysisTab artifacts={output.analysis_artifacts}
            executionId={executionId} projectName={execution?.project_name} />
        </div>
      ) : (
        <>
          <div
            id="tabpanel-pr"
            role="tabpanel"
            aria-labelledby="tab-pr"
            style={{ display: effectiveTab === 'pr' ? 'block' : 'none' }}
          >
            <PrTab pr={output.pull_request} prs={output.pull_requests} />
          </div>

          <div
            id="tabpanel-code"
            role="tabpanel"
            aria-labelledby="tab-code"
            style={{ display: effectiveTab === 'code' ? 'block' : 'none' }}
          >
            <CodeArtifactsTab
              executionId={executionId}
              initialArtifacts={output.code_artifacts}
              artifactsTotal={artifactsTotal}
            />
          </div>

          {MOSTRAR_ENGINEERING_STANDARDS && (
            <div
              id="tabpanel-compliance"
              role="tabpanel"
              aria-labelledby="tab-compliance"
              style={{ display: effectiveTab === 'compliance' ? 'block' : 'none' }}
            >
              <ComplianceTab checks={output.compliance_checks} executionId={executionId} />
            </div>
          )}
        </>
      )}

      <div
        id="tabpanel-audit"
        role="tabpanel"
        aria-labelledby="tab-audit"
        style={{ display: effectiveTab === 'audit' ? 'block' : 'none' }}
      >
        <AuditTab events={output.audit_events} executionId={executionId} />
      </div>

      {!isAnalysis && (
        <div
          id="tabpanel-replay"
          role="tabpanel"
          aria-labelledby="tab-replay"
          style={{ display: effectiveTab === 'replay' ? 'block' : 'none' }}
        >
          <ReplayTab events={output.replay_events} executionId={executionId} />
        </div>
      )}
    </div>
  );
}
